<template>
  <div v-if="visible" class="event-dropdown-menu" @click.stop>
    <!-- 标题 -->
    <div class="event-dropdown-title">赛事筛选</div>
    
    <!-- 内容区域 -->
    <div class="event-dropdown-content">
      <!-- 热门区域 -->
      <div class="event-section">
        <div class="event-section-title">热门</div>
        <div class="event-checkbox-grid">
          <div 
            v-for="event in hotEvents" 
            :key="event.id" 
            class="event-checkbox-item"
            :class="{ 'is-selected': event.selected }"
          >
            <input 
              type="checkbox" 
              :id="`event-hot-${event.id}`"
              :checked="event.selected"
              @change="toggleEventSelection(event, 'hot')"
              class="event-checkbox"
            />
            <label 
              :for="`event-hot-${event.id}`"
              class="event-label"
            >
              {{ event.name }}
              <span class="event-count">[{{ event.count }}]</span>
            </label>
          </div>
        </div>
      </div>
      
      <!-- 更多区域 -->
      <div class="event-section">
        <div class="event-section-title">更多</div>
        <div class="event-checkbox-grid">
          <div 
            v-for="event in moreEvents" 
            :key="event.id" 
            class="event-checkbox-item"
            :class="{ 'is-selected': event.selected }"
          >
            <input 
              type="checkbox" 
              :id="`event-more-${event.id}`"
              :checked="event.selected"
              @change="toggleEventSelection(event, 'more')"
              class="event-checkbox"
            />
            <label 
              :for="`event-more-${event.id}`"
              class="event-label"
            >
              {{ event.name }}
              <span class="event-count">[{{ event.count }}]</span>
            </label>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 底部按钮栏 -->
    <div class="event-dropdown-footer">
      <button class="event-footer-btn" @click="handleSelectAll">全选</button>
      <button class="event-footer-btn" @click="handleInvertSelection">反选</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  // 从父组件接收比赛数据
  matches: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:visible', 'confirm'])

/**
 * 处理点击外部区域关闭下拉框
 */
const handleClickOutside = (event) => {
  // 检查点击是否在下拉框外部
  const dropdown = event.target.closest('.event-dropdown-menu')
  const trigger = event.target.closest('.action_event_dropdown')
  
  if (!dropdown && !trigger && props.visible) {
    emit('update:visible', false)
  }
}

// 监听visible变化，添加/移除全局点击事件
watch(() => props.visible, (newVal) => {
  if (newVal) {
    // 延迟添加监听，避免立即触发
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside)
    }, 0)
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
})

// 组件卸载时移除监听
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

// 热门赛事数据
const hotEvents = ref([])

// 更多赛事数据
const moreEvents = ref([])

/**
 * 从比赛数据中提取赛事信息
 */
const extractEventsFromMatches = (matches) => {
  if (!matches || matches.length === 0) {
    return { hot: [], more: [] }
  }

  // 统计每个赛事的比赛数量
  const eventMap = new Map()
  
  matches.forEach((match) => {
    const leagueName = match.league_name || match.title
    const leagueId = match.league_id || match.category_id || leagueName
    
    if (!leagueName) return
    
    if (eventMap.has(leagueName)) {
      const event = eventMap.get(leagueName)
      event.count++
    } else {
      eventMap.set(leagueName, {
        id: leagueId,
        name: leagueName,
        count: 1,
        selected: true
      })
    }
  })

  // 转为数组并按比赛数量排序
  const allEvents = Array.from(eventMap.values()).sort((a, b) => b.count - a.count)
  
  // 前7个作为热门，其余作为更多
  const hotCount = 7
  return {
    hot: allEvents.slice(0, hotCount),
    more: allEvents.slice(hotCount)
  }
}

/**
 * 监听比赛数据变化，更新赛事列表
 */
watch(() => props.matches, (newMatches) => {
  if (newMatches && newMatches.length > 0) {
    const { hot, more } = extractEventsFromMatches(newMatches)
    hotEvents.value = hot
    moreEvents.value = more
  }
}, { immediate: true, deep: true })

/**
 * 切换赛事选中状态
 */
const toggleEventSelection = (event, section) => {
  event.selected = !event.selected
  // 实时触发筛选
  const selectedEvents = getSelectedEvents()
  emit('confirm', selectedEvents)
}

/**
 * 全选所有赛事
 */
const handleSelectAll = () => {
  hotEvents.value.forEach(event => {
    event.selected = true
  })
  moreEvents.value.forEach(event => {
    event.selected = true
  })
  // 实时触发筛选
  const selectedEvents = getSelectedEvents()
  emit('confirm', selectedEvents)
}

/**
 * 反选所有赛事
 */
const handleInvertSelection = () => {
  hotEvents.value.forEach(event => {
    event.selected = !event.selected
  })
  moreEvents.value.forEach(event => {
    event.selected = !event.selected
  })
  // 实时触发筛选
  const selectedEvents = getSelectedEvents()
  emit('confirm', selectedEvents)
}

/**
 * 获取所有选中的赛事
 */
const getSelectedEvents = () => {
  const selectedHot = hotEvents.value.filter(e => e.selected)
  const selectedMore = moreEvents.value.filter(e => e.selected)
  return {
    hot: selectedHot,
    more: selectedMore,
    ids: [...selectedHot.map(e => e.id), ...selectedMore.map(e => e.id)],
    names: [...selectedHot.map(e => e.name), ...selectedMore.map(e => e.name)]
  }
}

</script>

<style scoped lang="scss">
.event-dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  width: 460px;
  max-height: 600px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #E2EEFF;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  // 标题
  .event-dropdown-title {
    padding: 12px 20px;
    text-align: center;
    font-size: 15px;
    font-weight: 600;
    color: #525A79;
    border-bottom: 1px solid #E2EEFF;
    background: #fff;
  }

  // 内容区域
  .event-dropdown-content {
    padding: 16px 20px;
    overflow-y: auto;
    flex: 1;
    max-height: 450px;

    .event-section {
      margin-bottom: 24px;

      &:last-child {
        margin-bottom: 0;
      }

      .event-section-title {
        font-size: 14px;
        font-weight: 600;
        color: #525A79;
        margin-bottom: 12px;
      }
    }

    // 复选框网格布局（3列）
    .event-checkbox-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px 16px;

      .event-checkbox-item {
        display: flex;
        align-items: center;
        cursor: pointer;

        .event-checkbox {
          width: 16px;
          height: 16px;
          margin-right: 8px;
          cursor: pointer;
          appearance: none;
          border: 2px solid #d9d9d9;
          border-radius: 50%;
          position: relative;
          transition: all 0.2s ease;
          flex-shrink: 0;

          &:checked {
            background: $color-primary;
            border-color: $color-primary;
            
            &::after {
              content: '✓';
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              color: white;
              font-size: 11px;
              font-weight: bold;
            }
          }

          &:hover {
            border-color: $color-primary;
          }
        }

        .event-label {
          font-size: 13px;
          color: #525a79;
          font-weight: normal;
          cursor: pointer;
          display: flex;
          align-items: center;
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          transition: color 0.2s ease;

          .event-count {
            margin-left: 4px;
            color: #afb8cb;
            font-size: 10px;
            flex-shrink: 0;
          }
        }

        &.is-selected .event-label {
          color: $color-primary;

          .event-count {
            color: #afb8cb;
          }
        }
      }
    }
  }

  // 底部按钮栏
  .event-dropdown-footer {
    padding: 10px 20px;
    border-top: 1px solid #E2EEFF;
    background: #fff;
    display: flex;
    justify-content: center;
    gap: 10px;
    flex-shrink: 0;

    .event-footer-btn {
      padding: 6px 24px;
      font-size: 13px;
      font-weight: 500;
      color: #525A79;
      background: #fff;
      border: 1px solid #E2EEFF;
      border-radius: 16px;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        border-color: $color-primary;
        color: $color-primary;
      }
    }
  }
}
</style>
